import React, {useContext, useState} from "react"
import {IUser, IUserRoleEnum} from "../../types/user";
import npiApi from "../../services/api";
import NpiModalEdit from "../../components/modal-edit";
import {Button, Col, Form, Image, Input, Modal, notification, Row, Select, Switch} from "antd";
import {DeleteFilled, DeleteOutlined, EditOutlined, ReloadOutlined} from "@ant-design/icons";
import NpiInputSelectRole from "../../components/input/select-role";
import NpiInputSelectRegion from "../../components/input/select-region";
import NpiInputSelectCountry from "../../components/input/select-country";
import {NpiInternalContext} from "../../contexts/internal-context";
import {useTranslation} from "react-i18next";

const NpiUserEdit = ({user, onUpdate, onDelete}: {user:IUser, onUpdate:(u:IUser)=>void, onDelete:(u:IUser)=>void}) => {

    // local user just for the edit modal (not impacting real user until we save it / close the modal reset it to its original state)
    const [myUser, setMyUser] = useState(user);
    const {t} = useTranslation();

    // update form
    const [selectedRegion, setSelectedRegion] = useState<number>(myUser.region_id);
    const [selectedRole, setSelectedRole] = useState<number>(myUser.role);

    //Merchandising group
    const {merchandisingGroups} = useContext(NpiInternalContext);
    const merchGroupOptions = merchandisingGroups.filter(v => v.region_id === selectedRegion).map(m => ({value: m.id, label: m.name}));

    // edit User
    const editUser = (editUser:IUser) => npiApi.internal.user.save(editUser)
        .then((savedUser:IUser) => {
          notification.success({message: t('INTERNAL.CLIENT.USERS.SUCCESSFULLY_SAVED', {firstname: savedUser.firstname, lastname: savedUser.lastname})});
          setMyUser(savedUser);
          onUpdate(savedUser);
        });

    // reset form with local datas after a fetch from ldap server but cancel without saving
    const resetEdit = () => {
        setMyUser(user);
        setSelectedRegion(user.region_id);
        setSelectedRole(user.role)
    };

    // refresh User from ldap
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const refreshUser = () => {
        setRefreshing(true);
        npiApi.internal.user.refresh(user.id)
            .then((refreshedUser:IUser) => {
                notification.info({message: t('INTERNAL.CLIENT.USERS.USER_FETCHED_FROM_APPLE_DIRECTORY')});
                setMyUser(refreshedUser);
            })
            .finally(() => setRefreshing(false))
    };

    //delete User
    const showDeleteConfirm = () => {
        Modal.confirm({
            title: t('INTERNAL.CLIENT.USERS.DELETE_USER'),
            icon: <DeleteOutlined/>,
            okText:  t('INTERNAL.CLIENT.COMMON.YES'), okButtonProps: {danger: true},
            onOk: () => {
                npiApi.internal.user.delete(user)
                    .then((deletedUser: IUser) => {
                        notification.success({message: t('INTERNAL.CLIENT.USERS.SUCCESSFULLY_DELETED', {firstname: deletedUser.firstname, lastname: deletedUser.lastname})});
                        onDelete(deletedUser);
                    })
            }
        });
    };


    return <NpiModalEdit button={<Button title="Edit" icon={<EditOutlined/>} type={"primary"}/>}
                  title={t('INTERNAL.CLIENT.USERS.EDIT')}
                  value={myUser}
                  onChange={editUser}
                  onClose={resetEdit}
                  editable={true}
                  okText={t('COMMON.SAVE')}
                  footerLeft={<Button onClick={showDeleteConfirm} danger={true} type={'primary'} icon={<DeleteFilled/>}>{t('COMMON.DELETE')}</Button>}
                  formProps={{layout: 'vertical', labelCol: {span: 24}, wrapperCol: {span: 24}}}>
        <>
            <Image
                height={50}
                src={"data:image/jpg;base64,"+myUser.photo}
                fallback="data:image/png;base64,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"
            />

            <Form.Item label={t('INTERNAL.CLIENT.USERS.PERSON_ID')}>
              <Row gutter={8}>
                <Col flex="auto">
                  <Form.Item name="dsid">
                    <Input placeholder={"# " + t('INTERNAL.CLIENT.COMMON.DSID')}/>
                  </Form.Item>
                </Col>
                <Col flex="100px">
                  <Button type="primary" loading={refreshing} onClick={refreshUser} icon={<ReloadOutlined/>}>{t('INTERNAL.CLIENT.USERS.REFRESH_FROM_APPLE_DIRECTORY')}</Button>
                </Col>
              </Row>
            </Form.Item>

            <Form.Item name="firstname" label={t('INTERNAL.CLIENT.COMMON.FIRSTNAME')}>
                <Input placeholder={t('INTERNAL.CLIENT.COMMON.FIRSTNAME')+"..."}/>
            </Form.Item>

            <Form.Item name="lastname" label={t('INTERNAL.CLIENT.COMMON.LASTNAME')}>
                <Input placeholder={t('INTERNAL.CLIENT.COMMON.LASTNAME')+"..."}/>
            </Form.Item>

            <Form.Item name="email" label={t('INTERNAL.CLIENT.COMMON.EMAIL')}>
                <Input placeholder={t('INTERNAL.CLIENT.COMMON.EMAIL')+"..."}/>
            </Form.Item>

            <Form.Item name="role" label={t('INTERNAL.CLIENT.COMMON.ROLE')}>
                <NpiInputSelectRole onChange={setSelectedRole} restrict={true} placeholder={t('INTERNAL.CLIENT.COMMON.ROLE')+"..."}/>
            </Form.Item>

            {[IUserRoleEnum.COUNTRY_USER, IUserRoleEnum.USER].indexOf(selectedRole) >= 0 &&
                <Form.Item name="is_allowed_dashboard" label={t('INTERNAL.CLIENT.USERS.DASHBOARD_ALLOWED')} valuePropName="checked">
                    <Switch/>
                </Form.Item>
            }

            <Form.Item name="region_id" label={t('INTERNAL.CLIENT.COMMON.REGION')}>
                <NpiInputSelectRegion onChange={setSelectedRegion} restrict={true} placeholder={t('INTERNAL.CLIENT.COMMON.REGION')+"..."}/>
            </Form.Item>

            {/* if role is merchandising vendor, display group selection */
                [IUserRoleEnum.MERCHANDISING_VENDOR].indexOf(selectedRole) >= 0 && <>
                    <Form.Item name="merchandising_group_id" label={t('INTERNAL.CLIENT.COMMON.MERCHANDISING_GROUP')}>
                    <Select placeholder={t('INTERNAL.CLIENT.COMMON.MERCHANDISING_GROUP')} allowClear={true} options={merchGroupOptions}/>
                </Form.Item>
            </>}

            {/* if role is country restricted, display countries */
                [IUserRoleEnum.COUNTRY_USER, IUserRoleEnum.MERCHANDISING_VENDOR].indexOf(selectedRole) >= 0 && <>
                <Form.Item name="countries" label={t('INTERNAL.CLIENT.COMMON.COUNTRIES')}>
                    <NpiInputSelectCountry inRegions={[selectedRegion]} mode={'multiple'} placeholder={t('INTERNAL.CLIENT.COMMON.COUNTRIES')} allowClear={true} grouped={true}/>
                </Form.Item>
            </>}

        </>
    </NpiModalEdit>
};

export default NpiUserEdit;